import { z } from 'zod';
/**
 * Domain validation and normalization for website domains
 * (`technical.md`, Shared validation functions). Used by both the backend
 * and the dashboard so rules cannot drift.
 */
/** The maximum number of domains in a single save operation. */
export const MAX_DOMAINS_PER_SAVE = 50;
/**
 * Normalizes and validates a domain hostname.
 *
 * 1. Trims whitespace.
 * 2. Strips a leading `http://` or `https://` (UX convenience).
 * 3. Strips a trailing `/` and any path or query.
 * 4. Lowercases.
 * 5. Rejects if: empty, longer than 253 characters, contains whitespace,
 *    fewer than two labels, any label starts or ends with a hyphen, any
 *    label is empty (consecutive dots), or the input is an IP address.
 * 6. Returns the normalized domain, or `null` on failure.
 */
export function parseDomain(input) {
    let value = input.trim();
    // Strip protocol
    value = value.replace(/^https?:\/\//i, '');
    // Strip trailing slash and any path/query/fragment
    const slashIndex = value.indexOf('/');
    if (slashIndex !== -1)
        value = value.substring(0, slashIndex);
    const queryIndex = value.indexOf('?');
    if (queryIndex !== -1)
        value = value.substring(0, queryIndex);
    const hashIndex = value.indexOf('#');
    if (hashIndex !== -1)
        value = value.substring(0, hashIndex);
    // Strip port
    const portIndex = value.lastIndexOf(':');
    if (portIndex !== -1) {
        const possiblePort = value.substring(portIndex + 1);
        if (/^\d+$/.test(possiblePort)) {
            value = value.substring(0, portIndex);
        }
    }
    value = value.toLowerCase();
    if (value.length === 0)
        return null;
    if (value.length > 253)
        return null;
    if (/\s/.test(value))
        return null;
    // Reject IP addresses (v4)
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(value))
        return null;
    // Reject IPv6 in brackets
    if (value.startsWith('['))
        return null;
    const labels = value.split('.');
    if (labels.length < 2)
        return null;
    for (const label of labels) {
        if (label.length === 0)
            return null;
        if (label.startsWith('-') || label.endsWith('-'))
            return null;
        if (!/^[a-z0-9-]+$/.test(label))
            return null;
    }
    return value;
}
/**
 * A zod schema for a single domain. Transforms the input through
 * `parseDomain` and rejects `null` results.
 */
export const domainSchema = z
    .string()
    .transform((value) => parseDomain(value))
    .refine((value) => value !== null, {
    message: 'Enter a valid domain, e.g. example.com.',
});
//# sourceMappingURL=domain.js.map